import React from 'react';
import loadable from '@loadable/component';

import { LayoutComposer, TermlyEmbed } from '../../components/index.js';
import TopbarContainer from '../TopbarContainer/TopbarContainer.js';
import FooterContainer from '../FooterContainer/FooterContainer.js';
import StaticPage from '../PageBuilder/StaticPage.js';

import { H1 } from '../PageBuilder/Primitives/Heading';
import { fallbackSections } from './FallbackPage';
import { ASSET_NAME } from './TermsOfServicePage.duck';

import css from '../PageBuilder/PageBuilder.module.css';

// Termly-hosted Terms of Service document id (Console page content is no longer
// used to render this route — see TermsOfServiceContent below for the modal path).
const TERMS_OF_SERVICE_TERMLY_ID = '900dacac-f77b-4d2d-994b-31ea254a1378';

const SectionBuilder = loadable(
  () => import(/* webpackChunkName: "SectionBuilder" */ '../PageBuilder/PageBuilder'),
  {
    resolveComponent: components => components.SectionBuilder,
  }
);

// This "content-only" component can be used in modals etc.
// NOTE: The signup/terms modal (AuthenticationPage) still renders this from the
// Console-hosted asset, so it is intentionally left on the PageBuilder/markdown
// path and is NOT switched to the Termly embed.
const TermsOfServiceContent = props => {
  const { inProgress, error, data, featuredListings } = props;

  // We don't want to add h1 heading twice to the HTML (SEO issue).
  // Modal's header is mapped as h2
  const hasContent = data => typeof data?.content === 'string';
  const exposeContentAsChildren = data => {
    return hasContent(data) ? { children: data.content } : {};
  };

  if (!hasContent && inProgress) {
    return null;
  }

  const CustomHeading1 = props => <H1 as="h2" {...props} />;

  const hasData = error === null && data;
  const sectionsData = hasData ? data : fallbackSections;

  return (
    <SectionBuilder
      {...sectionsData}
      options={{
        featuredListings,
        fieldComponents: {
          heading1: { component: CustomHeading1, pickValidProps: exposeContentAsChildren },
        },
        isInsideContainer: true,
      }}
    />
  );
};

// Presentational component for TermsOfServicePage.
// The full page renders a Termly-hosted embed inside the standard page chrome
// (Topbar / Footer). The Console-hosted page content is no longer used for this
// route; the embed is client-rendered and independent of the page asset fetch.
const TermsOfServicePageComponent = () => {
  const layoutAreas = `
    topbar
    main
    footer
  `;
  return (
    <StaticPage
      title="Terms of Service"
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: 'Terms of Service',
      }}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="TermsOfServicePage" />
              </Topbar>
              <Main as="main" id="main-content" className={css.main}>
                <TermlyEmbed dataId={TERMS_OF_SERVICE_TERMLY_ID} />
              </Main>
              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </StaticPage>
  );
};

const TermsOfServicePage = TermsOfServicePageComponent;

const TOS_ASSET_NAME = ASSET_NAME;
export { TOS_ASSET_NAME, TermsOfServicePageComponent, TermsOfServiceContent };

export default TermsOfServicePage;
